import { type FC, type PropsWithChildren } from 'react';

import { Box } from '../Box';
import { Text } from '../Typography';
import {
  availabilityBadgeSmallLabelStyles,
  availabilityBadgeSmallStyles,
  availabilityBadgeVariantStyles,
} from './AvailabilityBadge.styles';
import { AvailabilityBadgeVariant } from './AvailabilityBadge.types';

export type AvailabilityBadgeSize = 'sm' | 'md';

export type AvailabilityBadgeProps = {
  /**
   * Visual variant of the badge
   */
  variant: AvailabilityBadgeVariant;
  /**
   * `sm` is a single-line pill. `md` is the default status badge.
   */
  size?: AvailabilityBadgeSize;
  /**
   * Additional CSS class names
   */
  className?: string;
  /**
   * Content to display in the badge
   */
  children?: React.ReactNode;
};

const sizeLayout: Record<
  AvailabilityBadgeSize,
  { px: string; py: string; textSize: '2xs' | 'xs' }
> = {
  sm: {
    px: 'var(--spacing-2)',
    py: 'var(--spacing-05)',
    textSize: '2xs',
  },
  md: {
    px: 'var(--spacing-4)',
    py: 'var(--spacing-1)',
    textSize: 'xs',
  },
};

const AvailabilityBadge: FC<PropsWithChildren<AvailabilityBadgeProps>> = ({
  children,
  variant,
  size = 'md',
  className,
}) => {
  const layout = sizeLayout[size];

  return (
    <Box
      display="inline-flex"
      alignItems="center"
      px={layout.px}
      py={layout.py}
      borderRadius="var(--radius-round)"
      css={[
        availabilityBadgeVariantStyles[variant],
        size === 'sm' ? availabilityBadgeSmallStyles : undefined,
      ]}
      className={className}
    >
      <Text
        as="span"
        css={size === 'sm' ? availabilityBadgeSmallLabelStyles : undefined}
        fontWeight="bold"
        size={layout.textSize}
      >
        {children}
      </Text>
    </Box>
  );
};

export default AvailabilityBadge;
